<template>
    <div class="card">
        <div class="card-header">
            <h2>Novo Protocolo</h2>
            <span>Solicitação automática e online pelo estabelecimento</span>
        </div>

        <form @submit.prevent="salvarFormulario">
            <div class="form-grid">
                <div class="form-item col-6">
                    <label>Estabelecimento</label>
                    <select v-model="form.estabelecimentoId">
                        <option value="">Selecione</option>
                        <option
                            v-for="item in lsEstabelecimentos"
                            :key="item.id"
                            :value="item.id"
                        >
                            {{ item.nome }} - {{ item.nmsim }}
                        </option>
                    </select>
                </div>

                <div class="form-item col-3">
                    <label>Tipo de Solicitação</label>
                    <select v-model="form.tipoSolicitacao">
                        <option value="NOVO_REGISTRO">Novo registro</option>
                        <option value="ALTERACAO">Alteração</option>
                        <option value="CANCELAMENTO">Cancelamento</option>
                    </select>
                </div>

                <div class="form-item col-3">
                    <label>Classificação</label>
                    <select v-model="form.classificacao">
                        <option value="">Selecione</option>
                        <option
                            v-for="item in lsClassificacoes"
                            :key="item"
                            :value="item"
                        >
                            {{ item }}
                        </option>
                    </select>
                </div>

                <div class="form-item col-4">
                    <label>Produto</label>
                    <input v-model="form.produtoNome" type="text" placeholder="Nome do produto" />
                </div>

                <div class="form-item col-2">
                    <label>Versão / Revisão</label>
                    <input v-model="form.produtoVersao" type="text" placeholder="Ex.: 1.0" />
                </div>

                <div class="form-item col-3">
                    <label>Responsável</label>
                    <input v-model="form.responsavel" type="text" placeholder="Nome do responsável" />
                </div>

                <div class="form-item col-3">
                    <label>Arquivo do Rótulo</label>
                    <input type="file" @change="adicionarArquivo($event, 'Rótulo')" />
                </div>

                <div class="form-item col-12">
                    <label>Observações</label>
                    <textarea
                        v-model="form.observacoes"
                        rows="4"
                        placeholder="Informações complementares sobre a solicitação"
                    ></textarea>
                </div>
            </div>

            <div v-if="form.anexos.length" class="anexos-box">
                <h3>Anexos simulados</h3>
                <div class="anexos-lista">
                    <div class="anexo-item" v-for="(anexo, index) in form.anexos" :key="index">
                        <span>{{ anexo.nome }}</span>
                        <small>{{ anexo.tipo }}</small>
                    </div>
                </div>
            </div>

            <div v-if="msgErro" class="msg-erro">
                {{ msgErro }}
            </div>

            <div class="actions">
                <button type="button" class="btn btn-light" @click="limparFormulario">
                    Limpar
                </button>
                <button type="submit" class="btn btn-primary">
                    Gerar Protocolo
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'

const props = defineProps({
    lsEstabelecimentos: { type: Array, default: () => [] },
    lsClassificacoes: { type: Array, default: () => [] }
})

const emit = defineEmits(['salvar'])

const msgErro = ref('')

const form = reactive({
    estabelecimentoId: '',
    tipoSolicitacao: 'NOVO_REGISTRO',
    classificacao: '',
    produtoNome: '',
    produtoVersao: '',
    responsavel: '',
    observacoes: '',
    anexos: []
})

const estabelecimentoSelecionado = computed(() => {
    return props.lsEstabelecimentos.find(x => Number(x.id) === Number(form.estabelecimentoId)) || null
})

function adicionarArquivo(event, tipo) {
    const file = event.target.files?.[0]
    if (!file) return

    form.anexos.push({
        nome: file.name,
        tipo
    })

    event.target.value = ''
}

function validarFormulario() {
    if (!form.estabelecimentoId) return 'Informe o estabelecimento.'
    if (!form.classificacao) return 'Informe a classificação.'
    if (!form.produtoNome) return 'Informe o nome do produto.'
    if (!form.responsavel) return 'Informe o responsável.'
    return ''
}

function salvarFormulario() {
    msgErro.value = validarFormulario()
    if (msgErro.value) return

    emit('salvar', {
        ...form,
        estabelecimentoNome: estabelecimentoSelecionado.value?.nome || ''
    })

    limparFormulario()
}

function limparFormulario() {
    form.estabelecimentoId = ''
    form.tipoSolicitacao = 'NOVO_REGISTRO'
    form.classificacao = ''
    form.produtoNome = ''
    form.produtoVersao = ''
    form.responsavel = ''
    form.observacoes = ''
    form.anexos = []
    msgErro.value = ''
}
</script>

<style scoped>
.card {
    background: #ffffff;
    border: 1px solid #dfe4ea;
    border-radius: 10px;
    padding: 18px;
}

.card-header {
    margin-bottom: 16px;
}

.card-header h2 {
    margin: 0;
    font-size: 18px;
    color: #1f2937;
}

.card-header span {
    display: block;
    margin-top: 4px;
    color: #6b7280;
    font-size: 14px;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 12px;
}

.form-item {
    display: flex;
    flex-direction: column;
}

.col-12 { grid-column: span 12; }
.col-6 { grid-column: span 6; }
.col-4 { grid-column: span 4; }
.col-3 { grid-column: span 3; }
.col-2 { grid-column: span 2; }

.form-item label {
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
}

.form-item input,
.form-item select,
.form-item textarea {
    border: 1px solid #cfd6df;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    outline: none;
    background: #fff;
}

.form-item textarea {
    resize: vertical;
}

.anexos-box {
    margin-top: 16px;
    padding: 14px;
    background: #f9fafb;
    border: 1px dashed #cbd5e1;
    border-radius: 10px;
}

.anexos-box h3 {
    margin: 0 0 10px 0;
    font-size: 15px;
}

.anexos-lista {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.anexo-item {
    display: flex;
    flex-direction: column;
    padding: 8px 10px;
    border-radius: 8px;
    background: #eef2f7;
    min-width: 180px;
}

.anexo-item span {
    font-size: 13px;
    color: #111827;
}

.anexo-item small {
    color: #6b7280;
}

.msg-erro {
    margin-top: 16px;
    padding: 10px 12px;
    border-radius: 8px;
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
}

.actions {
    margin-top: 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.btn {
    border: none;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
}

.btn-light {
    background: #e5e7eb;
    color: #111827;
}

.btn-primary {
    background: #2563eb;
    color: #fff;
}

@media (max-width: 1100px) {
    .col-6,
    .col-4,
    .col-3,
    .col-2 {
        grid-column: span 12;
    }
}
</style>